/*
 * Collection page motion layer. GSAP 3 + ScrollTrigger (assets/gsap-min.js, deferred in <head>).
 *
 * Rules (same as cta-na-motion.js)
 * - Progressive enhancement: the page is complete and usable without this file. Only the header copy is
 *   pre-hidden (html.cta-col-motion, set before first paint by snippets/cta-col-motion-flag.liquid) so the
 *   intro can play without a flash; that flag is always removed, even if GSAP never loads.
 * - No GSAP, reduced motion, the theme's "reduce animations" setting or the theme editor = no motion.
 * - transform and opacity only, inline styles cleared afterwards. The header image is never faded (LCP).
 * - Product cards keep the CSS reveal in cta-collection.js; this file adds the header, chips, sidebar,
 *   help band and the in-place update touches (count tween, pills pop) via the cta-col:render event.
 */
(function () {
  'use strict';
  if (window.ctaColMotion) return;

  var html = document.documentElement;
  var EASE = 'power3.out';

  function allowed() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      !(document.body && document.body.hasAttribute('data-reduce-animations')) &&
      !(window.Shopify && window.Shopify.designMode);
  }

  function release() {
    html.classList.remove('cta-col-motion');
  }

  function all(scope, selector) {
    return scope ? Array.prototype.slice.call(scope.querySelectorAll(selector)) : [];
  }

  // Wraps each word in a masked span so it can slide up. aria-label keeps the heading readable.
  function splitWords(el) {
    if (!el) return [];
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    el.setAttribute('aria-label', text);
    el.textContent = '';
    return text.split(' ').map(function (word, i, words) {
      var mask = document.createElement('span');
      mask.className = 'cta-col-word';
      mask.setAttribute('aria-hidden', 'true');
      var inner = document.createElement('span');
      inner.textContent = word;
      mask.appendChild(inner);
      el.appendChild(mask);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return inner;
    });
  }

  function firstInt(el) {
    var m = el && el.textContent.match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  // Animates the first integer in an element's text from one number to another
  function countTo(el, from, to, duration, delay) {
    if (!el || from == null || to == null || from === to) return;
    var tpl = el.textContent;
    var obj = { v: from };
    window.gsap.to(obj, {
      v: to,
      duration: duration || 0.6,
      delay: delay || 0,
      ease: 'power2.out',
      onUpdate: function () { el.textContent = tpl.replace(/\d+/, Math.round(obj.v)); },
      onComplete: function () { el.textContent = tpl; },
    });
  }

  function fresh(el) {
    return el && el.getBoundingClientRect().top > window.innerHeight * 0.9;
  }

  /* ---------- Header intro ---------- */
  function intro(root) {
    var gsap = window.gsap;
    var head = root.querySelector('.cta-col__head');
    if (!head) return release();
    var title = head.querySelector('.cta-col__title');
    var count = head.querySelector('.cta-col__count');
    var small = all(head, '.cta-col__crumbs, .cta-col__eyebrow, .cta-col__count, .cta-col__intro');
    var visual = head.querySelector('.cta-col__visual');
    var chips = all(head, '.cta-col__family-label, .cta-col__chip');
    var words = splitWords(title);

    gsap.set(small, { autoAlpha: 0, y: 10 });
    gsap.set(words, { yPercent: 110 });
    gsap.set(chips, { autoAlpha: 0, x: 8 });
    // Transform only: the header image (often the LCP) is never hidden
    if (visual) gsap.set(visual, { scale: 1.035, transformOrigin: '50% 50%' });
    release();

    var tl = gsap.timeline({
      defaults: { ease: EASE },
      onComplete: function () {
        gsap.set(small.concat(words, chips), { clearProps: 'opacity,visibility,transform' });
      },
    });
    tl.to(small.slice(0, 2), { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.06 }, 0)
      .to(words, { yPercent: 0, duration: 0.8, stagger: 0.06, ease: 'power4.out' }, 0.05)
      .to(small.slice(2), { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.08 }, 0.25)
      .to(chips, { autoAlpha: 1, x: 0, duration: 0.45, stagger: 0.035 }, 0.35);
    if (visual) tl.to(visual, { scale: 1, duration: 1.1, ease: 'power2.out', clearProps: 'transform' }, 0);
    var n = firstInt(count);
    if (n > 3) countTo(count, Math.round(n * 0.6), n, 0.8, 0.25);
  }

  /* ---------- Scroll reveals ---------- */
  function reveals(root) {
    var gsap = window.gsap;

    var side = root.querySelector('.cta-col__side');
    if (side) {
      var parts = all(side, '.cta-col__side-head, .cta-col__group');
      gsap.from(parts, { autoAlpha: 0, y: 10, duration: 0.5, stagger: 0.05, delay: 0.3, ease: EASE, clearProps: 'opacity,visibility,transform' });
    }

    var help = root.querySelector('.cta-srp__help');
    if (fresh(help)) {
      var bits = all(help, '.cta-srp__help-title, .cta-srp__help-text, .cta-srp__help-actions > *');
      gsap.set(help, { opacity: 0, y: 24 });
      gsap.set(bits, { opacity: 0, y: 12 });
      window.ScrollTrigger.create({
        trigger: help,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.timeline({ defaults: { ease: EASE } })
            .to(help, { opacity: 1, y: 0, duration: 0.7, clearProps: 'opacity,transform' }, 0)
            .to(bits, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, clearProps: 'opacity,transform' }, 0.15);
        },
      });
    }
  }

  /* ---------- In-place updates (filters, sort, page) ---------- */
  function onRender(root, event) {
    var gsap = window.gsap;
    var d = event.detail || {};
    countTo(root.querySelector('.cta-col__count'), d.before, d.after, 0.6);

    var pills = all(root, '.cta-col__pill');
    if (pills.length) gsap.from(pills, { scale: 0.9, autoAlpha: 0, duration: 0.3, stagger: 0.03, ease: 'back.out(1.6)', clearProps: 'opacity,visibility,transform' });

    var badge = root.querySelector('[data-cta-col-badge]');
    if (badge && !badge.hidden && d.activeBefore !== d.activeAfter) {
      gsap.fromTo(badge, { scale: 0.6 }, { scale: 1, duration: 0.4, ease: 'back.out(2)', clearProps: 'transform' });
    }
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  }

  /* ---------- Boot ---------- */
  function start() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var root = document.querySelector('[data-cta-col]');
    if (!root || !gsap || !ScrollTrigger || !allowed()) return release();
    gsap.registerPlugin(ScrollTrigger);
    window.ctaColMotion = { ready: true };
    intro(root);
    reveals(root);
    root.addEventListener('cta-col:render', function (event) { onRender(root, event); });
    window.addEventListener('load', function () { ScrollTrigger.refresh(); }, { once: true });
  }

  // gsap-min.js is deferred in <head> and runs first; wait briefly in case it is late
  var tries = 0;
  function whenReady() {
    if (window.gsap && window.ScrollTrigger) start();
    else if (tries++ < 20) window.setTimeout(whenReady, 100);
    else release();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenReady);
  else whenReady();
})();
